import Box from '@mui/material/Box';
import type {
  GridCallbackDetails,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import type { SxProps, Theme } from '@mui/material';

import { checkIfIsArray, invariant } from '@e-socna/utils';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { NoData } from '../Illustrations/NoData';
import { DataGrid } from '../DataGrid';
import { Loading } from '../Loading';

import { useInitialGridState } from './hooks/useInitialGridState.hook';
import { useFindAllMaterialsQuery } from './hooks/useFindAllMaterialsQuery.hook';
import { getColumns } from './get-columns';

interface Props {
  readonly checkboxSelection?: boolean | undefined;
  readonly onRowSelectionModelChange?:
    | ((
        rowSelectionModel: Readonly<GridRowSelectionModel>,
        details: Readonly<GridCallbackDetails>,
      ) => void)
    | undefined;
  readonly rowSelectionModel?: GridRowSelectionModel | undefined;
  readonly sx?: SxProps<Theme>;
}

const DEFAULT_SX = { flexGrow: 1, minHeight: 0, width: '100%', pt: 2 };

export const MaterialList = ({
  checkboxSelection = false,
  onRowSelectionModelChange: handleRowSelectionModelChange,
  rowSelectionModel,
  sx = DEFAULT_SX,
}: Readonly<Props>) => {
  invariant(
    !checkboxSelection ||
      !!(handleRowSelectionModelChange && rowSelectionModel),
    'MaterialList: If checkboxSelection is true, onRowSelectionModelChange and rowSelectionModel must be provided.',
  );

  const { data, isSuccess } = useFindAllMaterialsQuery();

  const { genericStatuses } = useReferencesContext();

  const { initialGridState } = useInitialGridState();

  if (isSuccess) {
    const { findAllMaterials: materials } = data;

    return !materials.length ? (
      <NoData />
    ) : (
      <Box sx={checkIfIsArray(sx) ? sx : [sx]}>
        <DataGrid
          checkboxSelection={checkboxSelection}
          columns={getColumns(genericStatuses)}
          initialState={initialGridState}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          rowSelectionModel={rowSelectionModel}
          rows={materials}
        />
      </Box>
    );
  }

  return <Loading />;
};
